<template>
  <NTable
    v-if="items.length"
    :data="items"
    cell-padding="small"
    :column-splitter="false"
    :striped="false"
    class="hide-scroll-bar mb-2 rounded-none border-none"
    hover-alignment="right"
    hover-offset="20%"
    :identifier="(row) => `${row.instrumentIdentification}-${row.orderSideId}`"
    fixed-header
    @row-click="(row) => updateActiveIsin(row.instrumentIdentification)"
  >
    <template #hoveredElements="{ row }">
      <NButton
        size="mini"
        variant="fill"
        @click.stop="
          openNewOrderFormDialog({
            type: ORDER_TYPES.buy,
            symbol: {
              instrumentName: row.instrumentTitle,
              isin: row.instrumentIdentification,
              instrumentTypeCode: row.instrumentTypeCode,
            },
          })
        "
      >
        <NText variant="overline" weight="medium">
          {{ t('common.buy') }}
        </NText>
      </NButton>

      <NButton
        size="mini"
        color="red"
        variant="fill"
        class="mr-1"
        @click.stop="
          openNewOrderFormDialog({
            type: ORDER_TYPES.sell,
            symbol: {
              instrumentName: row.instrumentTitle,
              isin: row.instrumentIdentification,
              instrumentTypeCode: row.instrumentTypeCode,
            },
          })
        "
      >
        <NText variant="overline" weight="medium">
          {{ t('common.sell') }}
        </NText>
      </NButton>
    </template>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.order.order_type')"
      prop="orderSideId"
      sortable
      align="text-right"
    >
      <NText
        v-if="row.orderSideId === ORDER_SIDES.buy"
        variant="caption2"
        weight="medium"
        class="text-brand-primary"
      >
        {{ t('common.buy') }}
      </NText>
      <NText
        v-else
        variant="caption2"
        weight="medium"
        class="text-critical-primary"
      >
        {{ t('common.sell') }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.symbol')"
      prop="instrumentTitle"
      sortable
      align="text-right"
    >
      <NText variant="caption2" weight="medium">
        {{ row.instrumentTitle }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.quantity')"
      prop="quantity"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.quantity">
        <ChangePriceEffect :key="row.quantity" :price="row.quantity">
          <NText variant="caption2" weight="medium">
            {{ number }}
          </NText>
        </ChangePriceEffect>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('orders.summarize.tradesCount')"
      prop="tradeCount"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.tradeCount">
        <NText variant="caption2" weight="medium">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('orders.summarize.average')"
      prop="averagePrice"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.averagePrice" round>
        <NText variant="caption2" weight="medium">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('orders.summarize.wage')"
      prop="wage"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.wage" round>
        <NText variant="caption2" weight="medium">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('orders.summarize.sum')"
      prop="calculatedSum"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.calculatedSum" round>
        <NText variant="caption2" weight="medium">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>
  </NTable>

  <EmptyList
    v-else
    :title="t('orders.summarize.emptyListTitle')"
    class="h-full flex flex-col items-center justify-center"
    title-class="mt-3"
    @click.stop
  >
    <template #image>
      <Image src="empty_box" alt="not_found" class="w-28 h-max" />
    </template>
  </EmptyList>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { SummarizedOrder } from '@libs/api-reference';
import { ORDER_TYPES, ORDER_SIDES } from '@core/order';

import { useActiveSymbolIsin } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';

import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import NumberFormatter from '@/components/Number.vue';
import EmptyList from '@/components/EmptyList.vue';
import Image from '@/components/Image.vue';

type SummarizeOrdersTable = {
  items: Array<SummarizedOrder & { calculatedSum: number }>;
};

defineProps<SummarizeOrdersTable>();

const { t } = useI18n();

const { openNewOrderFormDialog } = useOrderDialogs();

const { updateActiveIsin } = useActiveSymbolIsin();
</script>
